/**
 * @module features/upload/upload-manager
 * @description Main orchestration module for file upload functionality
 */

import { logger } from '../../utils/logger.js';
import { fetchFileCategories, hasCategoriesLoaded } from './file-categories.js';
import {
    initializeUploadElements,
    showModal,
    hideModal,
    resetUploadUI,
    showUploadError,
    hideUploadError,
    updateUploadButton,
    setUploadingState,
    setOnCloseCallback,
    setOnUploadStartCallback,
    getDOMElements,
    applyUploadConstraintsToUI
} from './upload-modal.js';
import {
    setOnFilesChanged,
    clearSelectedFiles,
    renderUploadFilesList,
    shouldEnableUpload,
    getSelectedFiles,
    getUploadFailureSummary
} from './upload-form.js';
import { uploadFiles } from './upload-executor.js';
import { loadEntityFiles } from '../entity/entity-loader.js';

// Current upload context
let currentEntityType = null;
let currentEntityId = null;
let currentUser = null;

/**
 * Get current entity type for upload context
 * @returns {string|null} Current entity type
 */
export function getCurrentEntityType() {
    return currentEntityType;
}

/**
 * Initialize upload functionality
 * Should be called once when the app starts
 */
export function initializeUploadManager() {
    // Initialize modal elements
    initializeUploadElements();

    // Set up callbacks
    setOnCloseCallback(hideUploadModal);
    setOnUploadStartCallback(startFileUpload);

    // Set up files changed callback
    setOnFilesChanged(handleFilesChanged);
}

/**
 * Handle files changed event
 * @param {Array} files - Array of selected files
 * @param {Object} options - Optional error or other info
 */
function handleFilesChanged(files, options = {}) {
    const { uploadFilesContainer, uploadFilesList } = getDOMElements();

    // Update UI
    renderUploadFilesList(uploadFilesContainer, uploadFilesList);

    // Update button state
    updateUploadButton(shouldEnableUpload());

    // Show error if present, and clear any stale one once the selection changes
    // without a new rejection
    if (options.error) {
        showUploadError(options.error);
    } else {
        hideUploadError();
    }
}

/**
 * Show upload modal for entity
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @param {string} entityId - Entity ID
 * @param {Object} user - Firebase user object
 */
export async function showUploadModal(entityType = 'projects', entityId, user) {
    currentEntityType = entityType;
    currentEntityId = entityId;
    currentUser = user;

    // Clear previous files
    clearSelectedFiles();

    try {
        // Clear any previous error
        hideUploadError();

        if (!user) {
            throw new Error('User not authenticated');
        }

        // Fetch categories if not already loaded. The same response carries the
        // upload constraints, so this populates both.
        if (!hasCategoriesLoaded()) {
            await fetchFileCategories(user);
        }

        // Reflect whatever constraints the API reported (or the permissive defaults)
        applyUploadConstraintsToUI();

        // Show modal
        showModal();

        // Reset UI
        resetUploadUI();

    } catch (error) {
        logger.error('Error showing upload modal:', error);
        showUploadError(`Failed to load upload dialog: ${error.message}`);
    }
}

/**
 * Hide upload modal
 */
export function hideUploadModal() {
    hideModal();
    clearSelectedFiles();
    resetUploadUI();

    // Clear context
    currentEntityType = null;
    currentEntityId = null;
    currentUser = null;
}

/**
 * Start file upload process
 */
async function startFileUpload() {
    const selectedFiles = getSelectedFiles();

    if (selectedFiles.length === 0) {
        showUploadError('No files selected');
        return;
    }

    if (!currentUser) {
        showUploadError('User not authenticated');
        return;
    }

    if (!currentEntityType || !currentEntityId) {
        showUploadError('Invalid upload context');
        return;
    }

    try {
        hideUploadError();
        setUploadingState(true);

        // Upload files with progress callback
        const result = await uploadFiles(
            currentEntityType,
            currentEntityId,
            handleUploadProgress
        );

        // Handle results
        if (result.success) {
            // Reload the files list after successful upload
            setTimeout(() => {
                reloadFilesAfterUpload();
            }, 1000);

            // Close modal if all uploads succeeded
            if (result.failureCount === 0) {
                setTimeout(() => {
                    hideUploadModal();
                }, 2000);
            }
        } else {
            // Surface the server's reason rather than a bare failure notice
            const failureSummary = getUploadFailureSummary();
            showUploadError(failureSummary
                ? `All uploads failed. ${failureSummary}`
                : 'All uploads failed');
        }

    } catch (error) {
        logger.error('Upload error:', error);
        showUploadError(`Upload failed: ${error.message}`);
    } finally {
        setUploadingState(false);
    }
}

/**
 * Handle upload progress updates
 * @param {Array} _files - Array of file items with status
 */
function handleUploadProgress(_files) {
    const { uploadFilesContainer, uploadFilesList } = getDOMElements();
    renderUploadFilesList(uploadFilesContainer, uploadFilesList);
}

/**
 * Reload files after successful upload
 */
async function reloadFilesAfterUpload() {
    if (!currentUser || !currentEntityType || !currentEntityId) {
        logger.warn('Cannot reload files: missing context');
        return;
    }

    try {
        await loadEntityFiles(currentUser, currentEntityType, currentEntityId);
    } catch (error) {
        logger.error('Error reloading files:', error);
    }
}

/**
 * Add upload button to metadata section
 * @param {string} entityType - Entity type
 * @param {string} entityId - Entity ID
 * @param {Object} user - Firebase user object
 */
export function addUploadButtonToMetadata(entityType, entityId, user) {
    if (!entityId || !entityType || !user) {
        return;
    }

    // Check if button already exists
    if (document.getElementById('upload-files-btn')) {
        return;
    }

    const uploadBtn = document.createElement('button');
    uploadBtn.id = 'upload-files-btn';
    uploadBtn.className = 'upload-files-btn';
    uploadBtn.innerHTML = '<span>📤</span><span>Upload Files</span>';
    uploadBtn.addEventListener('click', () => showUploadModal(entityType, entityId, user));

    // Add button to metadata section
    const metadataSection = document.getElementById('file-metadata');
    if (metadataSection) {
        metadataSection.appendChild(uploadBtn);
    }
}

/**
 * Get current upload context (for debugging)
 * @returns {Object} Current context
 */
export function getUploadContext() {
    return {
        entityType: currentEntityType,
        entityId: currentEntityId,
        hasUser: !!currentUser
    };
}
